import { queryClient, reportGqlError, request, useMutation } from '../../api'
import {
  ServerCardTemplate,
  ServerCardTemplateInsertInput,
  ServerCardTemplateMutationResponse,
  ServerCardTemplateServiceInsertInput,
  ServerCardTemplateServiceMutationResponse,
  ServerCardTemplateSetInput,
} from '../../generated/graphql-types'
import { CARD_TEMPLATES_KEY } from './queryKeys'
import { gql } from 'graphql-request'
import { ARCHIVED_CARD_TEMPLATES_QUERY_KEY } from '../card/queryKeys'
import { CARD_TYPES } from './logic'

async function requestCreateCardTemplate(
  cardTemplateInsertInput: ServerCardTemplateInsertInput,
): Promise<{ insertCardTemplate?: { id?: number } }> {
  return request(
    gql`
      mutation ($cardTemplateInsertInput: cardTemplate_insert_input!) {
        insertCardTemplate(object: $cardTemplateInsertInput) {
          id
        }
      }
    `,
    { cardTemplateInsertInput },
  )
}

export function useCreateSubscriptionTemplate() {
  const createCard = (
    cardCardTemplateInsertInput: ServerCardTemplateInsertInput &
      Required<
        Pick<
          ServerCardTemplateInsertInput,
          | 'name'
          | 'cardPeriod'
          | 'price'
          | 'cardTemplateServices'
          | 'availableToAllBranches'
          | 'subscriptionTypeVariation'
          | 'sharedQuantity'
        >
      >,
  ) => requestCreateCardTemplate({ ...cardCardTemplateInsertInput, type: CARD_TYPES.subscription })

  return useMutation(createCard, {
    onSuccess: () => queryClient.invalidateQueries([CARD_TEMPLATES_KEY]),
    onError: reportGqlError,
  })
}

export function useCreateGiftCardTemplate() {
  const createCard = (
    cardCardTemplateInsertInput: Required<
      Pick<ServerCardTemplateInsertInput, 'name' | 'cardPeriod' | 'price' | 'branchId'>
    >,
  ) => requestCreateCardTemplate({ ...cardCardTemplateInsertInput, type: CARD_TYPES.giftCard })

  return useMutation(createCard, {
    onSuccess: () => queryClient.invalidateQueries([CARD_TEMPLATES_KEY]),
    onError: reportGqlError,
  })
}

async function requestUpdateCardTemplate(dto: {
  id: number
  cardTemplateSetInput: ServerCardTemplateSetInput
  cardTemplateServiceInsertInputs?: ServerCardTemplateServiceInsertInput[]
}): Promise<{
  updateCardTemplateById?: ServerCardTemplate
  deleteCardServices?: ServerCardTemplateServiceMutationResponse
  insertCardServices?: ServerCardTemplateServiceMutationResponse
}> {
  return request(
    gql`
      mutation (
        $id: Int!
        $cardTemplateSetInput: cardTemplate_set_input!
        $cardTemplateServiceInsertInputs: [cardTemplateService_insert_input!]!
        $includeUpdateCardServices: Boolean!
      ) {
        updateCardTemplateById(pk_columns: { id: $id }, _set: $cardTemplateSetInput) {
          id
          name
          price
          cardPeriod
        }
        deleteCardTemplateServices(where: { cardTemplateId: { _eq: $id } })
          @include(if: $includeUpdateCardServices) {
          affected_rows
        }
        insertCardTemplateServices(objects: $cardTemplateServiceInsertInputs)
          @include(if: $includeUpdateCardServices) {
          returning {
            serviceId
            servicePrice
            serviceQuantity
          }
        }
      }
    `,
    {
      ...dto,
      cardTemplateServiceInsertInputs: dto.cardTemplateServiceInsertInputs ?? [],
      includeUpdateCardServices: Boolean(dto.cardTemplateServiceInsertInputs),
    },
  )
}

export function useUpdateSubscriptionTemplate() {
  const updateCard = (dto: {
    id: number
    cardTemplateSetInput: ServerCardTemplateSetInput
    cardTemplateServiceInsertInputs: ServerCardTemplateServiceInsertInput[]
  }) => requestUpdateCardTemplate(dto)

  return useMutation(updateCard, {
    onSuccess: () => queryClient.invalidateQueries([CARD_TEMPLATES_KEY]),
    onError: reportGqlError,
  })
}

export function useUpdateCardTemplateName() {
  return useMutation(
    async (dto: {
      id: number
      name: string
    }): Promise<{ updateCardTemplatesName: ServerCardTemplateMutationResponse }> => {
      return request(
        gql`
          mutation ($id: Int!, $name: String!) {
            updateCardTemplatesName(where: { id: { _eq: $id } }, _set: { name: $name }) {
              affected_rows
            }
          }
        `,
        dto,
      )
    },
    {
      onSuccess: () => queryClient.invalidateQueries([CARD_TEMPLATES_KEY]),
      onError: reportGqlError,
    },
  )
}

export function useUpdateGiftCardTemplate() {
  const updateCard = (dto: { id: number; cardTemplateSetInput: ServerCardTemplateSetInput }) =>
    requestUpdateCardTemplate(dto)

  return useMutation(updateCard, {
    onSuccess: () => queryClient.invalidateQueries([CARD_TEMPLATES_KEY]),
    onError: reportGqlError,
  })
}

export function useArchiveCard() {
  return useMutation(
    async (dto: {
      id: number
      archived: boolean
    }): Promise<{ updateCardTemplateById: { id: number } }> => {
      return request(
        gql`
          mutation ($id: Int!, $archived: timestamptz) {
            updateCardTemplateById(pk_columns: { id: $id }, _set: { archived: $archived }) {
              id
            }
          }
        `,
        { id: dto.id, archived: dto.archived ? new Date().toISOString() : null },
      )
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CARD_TEMPLATES_KEY])
        queryClient.invalidateQueries([ARCHIVED_CARD_TEMPLATES_QUERY_KEY])
      },
      onError: reportGqlError,
    },
  )
}
